import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { ToastProvider, useToast } from './components/ui/toast';
import { Folder, FolderOpen, Tag, Plus, Edit2, Trash2, Search, GripVertical } from 'lucide-react';
import NoteEditor from './NoteEditor';
import RichTextEditor from './RichTextEditor';
import Auth from './Auth';
import './App.css';

function App() {
  const [session, setSession] = useState(null);
  const [notes, setNotes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [tags, setTags] = useState([]);
  const [noteTags, setNoteTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [newTagName, setNewTagName] = useState('');
  const [draggedNoteId, setDraggedNoteId] = useState(null);
  const [dragOverFolderId, setDragOverFolderId] = useState(null);
  const [swipedNoteId, setSwipedNoteId] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const { addToast } = useToast();
  const notesListRef = useRef(null);
  
  // Modal state for folder deletion
  const [deleteFolderModal, setDeleteFolderModal] = useState({
    isOpen: false,
    folderId: null,
    folderName: '',
    noteCount: 0
  });
  
  // Drag state for swipe-to-delete
  const [dragState, setDragState] = useState({
    isDragging: false,
    startX: 0,
    currentX: 0,
    noteId: null
  });

  useEffect(() => {
    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });
  
    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });
  
    return () => subscription.unsubscribe();
  }, []);
  
  useEffect(() => {
    if (session) {
      fetchNotes();
      fetchFolders();
      fetchTags();
      fetchNoteTags();
    }
  }, [session]);

  // Close swipe when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notesListRef.current && !notesListRef.current.contains(event.target)) {
        setSwipedNoteId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Global mouse and touch events for drag functionality
  useEffect(() => {
    const handleGlobalMove = (e) => {
      if (dragState.isDragging) {
        handleSwipeMove(e);
      }
    };
  

    const handleGlobalEnd = (e) => {
      if (dragState.isDragging) {
        handleSwipeEnd(e);
      }
    };

    if (dragState.isDragging) {
      document.addEventListener('mousemove', handleGlobalMove);
      document.addEventListener('mouseup', handleGlobalEnd);
      document.addEventListener('touchmove', handleGlobalMove, { passive: false });
      document.addEventListener('touchend', handleGlobalEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMove);
      document.removeEventListener('mouseup', handleGlobalEnd);
      document.removeEventListener('touchmove', handleGlobalMove);
      document.removeEventListener('touchend', handleGlobalEnd);
    };
  }, [dragState.isDragging, dragState.noteId, dragState.startX, dragState.currentX]);


  const filteredNotes = notes.filter(note => {
    if (selectedFolder !== null && note.folder_id !== selectedFolder) {
      return false;
    }
    
    if (selectedTags.length > 0) {
      const noteTagIds = noteTags
        .filter(nt => nt.note_id === note.id)
        .map(nt => nt.tag_id);
      
      const hasAllTags = selectedTags.every(tagId => noteTagIds.includes(tagId));
      if (!hasAllTags) return false;
    }
    
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    const titleMatch = note.title?.toLowerCase().includes(query);
    const contentMatch = note.content?.toLowerCase().includes(query);
    
    return titleMatch || contentMatch;
  });

  // Remove this useEffect since we now fetch data when session is available

  async function fetchNotes() {
    if (!session?.user?.id) return;
    
    const { data, error } = await supabase
      .from('notes')
      .select('*')
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false });
    
    if (error) console.error('Error fetching notes:', error);
    else setNotes(data);
  }

  async function fetchFolders() {
    if (!session?.user?.id) return;
    
    const { data, error } = await supabase
      .from('folders')
      .select('*')
      .eq('user_id', session.user.id)
      .order('name', { ascending: true });
    
    if (error) console.error('Error fetching folders:', error);
    else setFolders(data);
  }

  async function fetchTags() {
    if (!session?.user?.id) return;
    
    const { data, error } = await supabase
      .from('tags')
      .select('*')
      .eq('user_id', session.user.id)
      .order('name', { ascending: true });
    
    if (error) console.error('Error fetching tags:', error);
    else setTags(data);
  }
  
  async function fetchNoteTags() {
    const { data, error } = await supabase
      .from('note_tags')
      .select('*');
    
    if (error) console.error('Error fetching note_tags:', error);
    else setNoteTags(data);
  }

  async function createTag() {
    if (!newTagName.trim()) return;
    if (!session?.user?.id) {
      addToast({
        type: 'error',
        title: 'Not authenticated',
        description: 'Please log in to create tags.',
      });
      return;
    }
    
    const existingTag = tags.find(t => t.name.toLowerCase() === newTagName.toLowerCase());
    if (existingTag) {
      addToast({
        type: 'error',
        title: 'Tag already exists',
        description: 'A tag with this name already exists.',
      });
      return;
    }
    
    const { data, error } = await supabase
      .from('tags')
      .insert([{ name: newTagName, user_id: session.user.id }])
      .select();
  
    if (error) {
      console.error('Error creating tag:', error);
      addToast({
        type: 'error',
        title: 'Error creating tag',
        description: error.message || 'Failed to create the tag. Please try again.',
      });
      return;
    } else {
      setTags([...tags, data[0]]);
      setNewTagName('');
      addToast({
        type: 'success',
        title: 'Tag created',
        description: `Tag "${newTagName}" created successfully.`,
      });
    }
  }

  async function toggleNoteTag(noteId, tagId) {
    const existing = noteTags.find(nt => nt.note_id === noteId && nt.tag_id === tagId);
    
    if (existing) {
      const { error } = await supabase
        .from('note_tags')
        .delete()
        .eq('id', existing.id);
      
      if (error) console.error('Error removing tag:', error);
      else setNoteTags(noteTags.filter(nt => nt.id !== existing.id));
    } else {
      const { data, error } = await supabase
        .from('note_tags')
        .insert([{ note_id: noteId, tag_id: tagId }])
        .select();
      
      if (error) console.error('Error adding tag:', error);
      else setNoteTags([...noteTags, data[0]]);
    }
  }

  async function createFolder() {
    if (!newFolderName.trim()) return;
    if (!session?.user?.id) {
      addToast({
        type: 'error',
        title: 'Not authenticated',
        description: 'Please log in to create folders.',
      });
      return;
    }
    
    const { data, error } = await supabase
      .from('folders')
      .insert([{ name: newFolderName, user_id: session.user.id }])
      .select();
  
    if (error) {
      console.error('Error creating folder:', error);
      addToast({
        type: 'error',
        title: 'Error creating folder',
        description: error.message || 'Failed to create the folder. Please try again.',
      });
      return;
    } else {
      setFolders([...folders, data[0]]);
      setNewFolderName('');
      setShowNewFolderInput(false);
      addToast({
        type: 'success',
        title: 'Folder created',
        description: `Folder "${newFolderName}" created successfully.`,
      });
    }
  }

  async function deleteTag(tagId) {
    if (!session?.user?.id) {
      addToast({
        type: 'error',
        title: 'Not authenticated',
        description: 'Please log in to delete tags.',
      });
      return;
    }

    const tag = tags.find(t => t.id === tagId);
    if (!tag) return;

    // First, remove all note-tag associations
    const { error: noteTagError } = await supabase
      .from('note_tags')
      .delete()
      .eq('tag_id', tagId);

    if (noteTagError) {
      console.error('Error removing note-tag associations:', noteTagError);
      addToast({
        type: 'error',
        title: 'Error deleting tag',
        description: 'Failed to remove tag associations. Please try again.',
      });
      return;
    }

    // Then delete the tag itself
    const { error } = await supabase
      .from('tags')
      .delete()
      .eq('id', tagId)
      .eq('user_id', session.user.id);

    if (error) {
      console.error('Error deleting tag:', error);
      addToast({
        type: 'error',
        title: 'Error deleting tag',
        description: error.message || 'Failed to delete the tag. Please try again.',
      });
    } else {
      setTags(tags.filter(t => t.id !== tagId));
      setNoteTags(noteTags.filter(nt => nt.tag_id !== tagId));
      addToast({
        type: 'success',
        title: 'Tag deleted',
        description: `Tag "${tag.name}" has been deleted.`,
      });
    }
  }

  async function deleteFolder(folderId) {
    if (!session?.user?.id) {
      addToast({
        type: 'error',
        title: 'Not authenticated',
        description: 'Please log in to delete folders.',
      });
      return;
    }

    const folder = folders.find(f => f.id === folderId);
    if (!folder) return;

    // Check if folder has notes
    const folderNotes = notes.filter(n => n.folder_id === folderId);
    if (folderNotes.length > 0) {
      // Show modal instead of preventing deletion
      setDeleteFolderModal({
        isOpen: true,
        folderId: folderId,
        folderName: folder.name,
        noteCount: folderNotes.length
      });
      return;
    }

    // If no notes, delete directly
    await performFolderDeletion(folderId, folder.name);
  }

  async function performFolderDeletion(folderId, folderName, moveToFolderId = null, deleteNotes = false) {
    if (!session?.user?.id) return;

    try {
      if (deleteNotes) {
        // Delete all notes in the folder first
        const folderNotes = notes.filter(n => n.folder_id === folderId);
        for (const note of folderNotes) {
          await supabase
            .from('notes')
            .delete()
            .eq('id', note.id)
            .eq('user_id', session.user.id);
        }
        setNotes(notes.filter(n => n.folder_id !== folderId));
      } else if (moveToFolderId !== null) {
        // Move notes to another folder
        const { error } = await supabase
          .from('notes')
          .update({ folder_id: moveToFolderId })
          .eq('folder_id', folderId)
          .eq('user_id', session.user.id);

        if (error) throw error;

        // Update local state
        setNotes(notes.map(note => 
          note.folder_id === folderId 
            ? { ...note, folder_id: moveToFolderId }
            : note
        ));
      }

      // Delete the folder
      const { error } = await supabase
        .from('folders')
        .delete()
        .eq('id', folderId)
        .eq('user_id', session.user.id);

      if (error) throw error;

      setFolders(folders.filter(f => f.id !== folderId));
      
      let message = `Folder "${folderName}" has been deleted.`;
      if (deleteNotes) {
        message += ` All notes in the folder have also been deleted.`;
      } else if (moveToFolderId !== null) {
        const targetFolder = folders.find(f => f.id === moveToFolderId);
        message += ` Notes have been moved to "${targetFolder?.name || 'All Notes'}".`;
      }

      addToast({
        type: 'success',
        title: 'Folder deleted',
        description: message,
      });
    } catch (error) {
      console.error('Error deleting folder:', error);
      addToast({
        type: 'error',
        title: 'Error deleting folder',
        description: error.message || 'Failed to delete the folder. Please try again.',
      });
    }
  }

  async function createNote() {
    if (!session?.user?.id) {
      addToast({
        type: 'error',
        title: 'Not authenticated',
        description: 'Please log in to create notes.',
      });
      return;
    }
    
    const { data, error } = await supabase
      .from('notes')
      .insert([
        { 
          title: 'Untitled Note', 
          content: '', 
          folder_id: selectedFolder || null,
          user_id: session.user.id
        }
      ])
      .select();

    if (error) {
      console.error('Error creating note:', error);
      addToast({
        type: 'error',
        title: 'Error creating note',
        description: 'Failed to create a new note. Please try again.',
      });
      return;
    }

    const newNote = data[0];
    setNotes([newNote, ...notes]);
    setSelectedNote(newNote);
    
    addToast({
      type: 'success',
      title: 'Note created',
      description: 'New note created successfully.',
    });
  }

  async function deleteNote(id) {
    const { error } = await supabase
      .from('notes')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting note:', error);
      addToast({
        type: 'error',
        title: 'Error deleting note',
        description: 'Failed to delete the note. Please try again.',
      });
    } else {
      setNotes(notes.filter(note => note.id !== id));
      // Clear selected note if it was deleted
      if (selectedNote && selectedNote.id === id) {
        setSelectedNote(null);
      }
      addToast({
        type: 'success',
        title: 'Note deleted',
        description: 'The note has been deleted successfully.',
      });
    }
  }

  function selectNote(note) {
    setSelectedNote(note);
  }

  function handleNoteSave(updatedNote) {
    setNotes(notes.map(note => 
      note.id === updatedNote.id ? updatedNote : note
    ));
    // Note: Toast notification removed since we're using auto-save
  }

  function handleNoteDelete(noteId) {
    setNotes(notes.filter(note => note.id !== noteId));
    // Clear selected note if it was deleted
    if (selectedNote && selectedNote.id === noteId) {
      setSelectedNote(null);
    }
  }

  async function updateNoteFolder(noteId, newFolderId) {
    const { error } = await supabase
      .from('notes')
      .update({ 
        folder_id: newFolderId,
        updated_at: new Date().toISOString()
      })
      .eq('id', noteId);
  
    if (error) {
      console.error('Error updating note folder:', error);
      return;
    }
    
    setNotes(notes.map(note => 
      note.id === noteId 
        ? { ...note, folder_id: newFolderId, updated_at: new Date().toISOString() }
        : note
    ));
  }

  // Drag and drop handlers
  function handleDragStart(e, note) {
    e.dataTransfer.setData('application/json', JSON.stringify(note));
    e.dataTransfer.effectAllowed = 'move';
    setDraggedNoteId(note.id);
  }

  function handleDragEnd() {
    setDraggedNoteId(null);
  }

  // Drag handlers for swipe-to-delete
  const handleSwipeStart = (e, noteId) => {
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    console.log('Swipe start - Note ID:', noteId, 'Start X:', clientX);
    setDragState({
      isDragging: true,
      startX: clientX,
      currentX: clientX,
      noteId: noteId
    });
  };

  const handleSwipeMove = (e) => {
    if (dragState.isDragging) {
      const clientX = e.clientX || (e.touches && e.touches[0].clientX);
      const dragDistance = dragState.startX - clientX;
      
      // Log when we cross the 60px threshold
      if (dragDistance > 60 && dragDistance < 65) {
        console.log('Crossed 60px threshold - delete button should appear!');
      }
      
      setDragState(prev => ({
        ...prev,
        currentX: clientX
      }));
    }
  };

  const handleSwipeEnd = (e) => {
    if (!dragState.isDragging) return;

    const dragDistance = dragState.startX - dragState.currentX;
    const showDeleteThreshold = 60; // Show delete button at 60px
    const deleteThreshold = 150; // Delete note at 150px (much longer drag)

    console.log('Swipe end - Distance:', dragDistance, 'Note ID:', dragState.noteId);

    if (dragDistance > deleteThreshold) {
      // Delete the note immediately
      console.log('Auto-deleting note:', dragState.noteId);
      deleteNote(dragState.noteId);
      setSwipedNoteId(null);
    } else if (dragDistance > showDeleteThreshold) {
      // Show delete button
      console.log('Showing delete button for note:', dragState.noteId);
      setSwipedNoteId(dragState.noteId);
    } else {
      // Reset if not enough drag distance
      console.log('Resetting - not enough drag distance');
      setSwipedNoteId(null);
    }

    // Reset drag state
    setDragState({
      isDragging: false,
      startX: 0,
      currentX: 0,
      noteId: null
    });
  };


  function handleDragOver(e, folderId) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverFolderId(folderId);
  }

  function handleDragLeave() {
    setDragOverFolderId(null);
  }

  function handleDrop(e, targetFolderId) {
    e.preventDefault();
    const noteData = JSON.parse(e.dataTransfer.getData('application/json'));
    
    if (noteData.folder_id !== targetFolderId) {
      updateNoteFolder(noteData.id, targetFolderId);
    }
    setDraggedNoteId(null);
    setDragOverFolderId(null);
  }


  if (!session) {
    return <Auth />;
  }

  return (
    <>
      <div className="flex h-screen bg-background overflow-x-hidden" style={{ width: '100vw', maxWidth: '100vw' }}>
      {/* Primary Sidebar for Folders */}
      <div className="border-r bg-muted/30 p-4 overflow-y-auto flex-shrink-0" style={{ width: '240px', minWidth: '240px' }}>
        <div className="mb-6">
          <h1 className="text-2xl font-bold mb-4">My Notes App</h1>
          <Button onClick={createNote} className="w-full transition-all-smooth hover-scale-102" style={{ backgroundColor: '#6366f1', color: 'white', fontWeight: '600' }}>
            <Plus className="h-4 w-4 mr-2" />
            Create Note
          </Button>
        </div>
        
        <h2 className="text-xl font-bold mb-4">Folders</h2>
        
        {/* All Notes */}
        <div
          onClick={() => setSelectedFolder(null)}
          onDragOver={(e) => handleDragOver(e, null)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, null)}
          className={`flex items-center gap-2 p-3 mb-1 rounded-md cursor-pointer transition-all-smooth hover:bg-muted/50 hover-scale-102 ${
            selectedFolder === null ? '' : 'bg-card active:bg-muted'
          } ${dragOverFolderId === null && draggedNoteId ? 'ring-2' : ''}`}
          style={{
            backgroundColor: selectedFolder === null ? '#F2F3FF' : '',
            color: selectedFolder === null ? '#332066' : '',
            fontWeight: selectedFolder === null ? '600' : '',
            border: selectedFolder === null ? '1px solid #5236C4' : (dragOverFolderId === null && draggedNoteId ? '2px solid #818cf8' : ''),
            boxShadow: selectedFolder === null ? '0 4px 6px -1px rgba(82, 54, 196, 0.1)' : ''
          }}
        >
          <Folder className="h-4 w-4" />
          <span>All Notes</span>
          <span className="ml-auto text-sm">({notes.length})</span>
        </div>
  
        {/* Folder List */}
        {folders.map(folder => (
          <div
            key={folder.id}
            className={`group flex items-center gap-2 p-3 mb-1 rounded-md cursor-pointer transition-all-smooth hover:bg-muted/50 hover-scale-102 ${
              selectedFolder === folder.id ? '' : 'bg-card active:bg-muted'
            } ${dragOverFolderId === folder.id && draggedNoteId ? 'ring-2' : ''}`}
            style={{
              backgroundColor: selectedFolder === folder.id ? '#F2F3FF' : '',
              color: selectedFolder === folder.id ? '#332066' : '',
              fontWeight: selectedFolder === folder.id ? '600' : '',
              border: selectedFolder === folder.id ? '1px solid #5236C4' : (dragOverFolderId === folder.id && draggedNoteId ? '2px solid #818cf8' : ''),
              boxShadow: selectedFolder === folder.id ? '0 4px 6px -1px rgba(82, 54, 196, 0.1)' : ''
            }}
          >
            <div 
              className="flex items-center gap-2 flex-1"
              onClick={() => setSelectedFolder(folder.id)}
              onDragOver={(e) => handleDragOver(e, folder.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, folder.id)}
            >
              <FolderOpen className="h-4 w-4" />
              <span>{folder.name}</span>
              <span className="ml-auto text-sm">({notes.filter(n => n.folder_id === folder.id).length})</span>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                deleteFolder(folder.id);
              }}
              className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1 hover:bg-red-100 rounded"
              style={{ color: '#dc2626' }}
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        ))}
  
        {/* New Folder Button */}
        {!showNewFolderInput && (
          <Button
            onClick={() => setShowNewFolderInput(true)}
            className="w-full mt-2 transition-all-smooth hover-scale-102"
            variant="default"
          >
            <Plus className="h-4 w-4" />
            New Folder
          </Button>
        )}
  
        {/* New Folder Input */}
        {showNewFolderInput && (
          <div className="mt-2 space-y-2">
            <Input
              type="text"
              placeholder="Folder name..."
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && createFolder()}
            />
            <div className="flex gap-2">
              <Button
                onClick={createFolder}
                className="flex-1 transition-all-smooth hover-scale-102"
                size="sm"
              >
                Create
              </Button>
              <Button
                onClick={() => {
                  setShowNewFolderInput(false);
                  setNewFolderName('');
                }}
                variant="outline"
                className="flex-1 transition-all-smooth hover-scale-102"
                size="sm"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Tags Section */}
        <div className="mt-6 pt-6 border-t">
          <h2 className="text-xl font-bold mb-4">Tags</h2>
          
          {/* All Tags filter button */}
          <div
            onClick={() => setSelectedTags([])}
            className={`flex items-center gap-2 p-2 mb-1 rounded-md cursor-pointer transition-all duration-200 ease-in-out hover:bg-muted/50 hover:scale-[1.02] ${
              selectedTags.length === 0 ? '' : 'bg-card active:bg-muted'
            }`}
            style={{
              backgroundColor: selectedTags.length === 0 ? '#F2F3FF' : '',
              color: selectedTags.length === 0 ? '#332066' : '',
              fontWeight: selectedTags.length === 0 ? '600' : '',
              border: selectedTags.length === 0 ? '1px solid #5236C4' : '',
              boxShadow: selectedTags.length === 0 ? '0 4px 6px -1px rgba(82, 54, 196, 0.1)' : ''
            }}
          >
            <Tag className="h-4 w-4" />
            <span className="text-sm">All Tags</span>
          </div>

          {/* Tag List */}
          {tags.map(tag => {
            const tagNoteCount = noteTags.filter(nt => nt.tag_id === tag.id).length;
            const isSelected = selectedTags.includes(tag.id);
            
            return (
              <div
                key={tag.id}
                className={`group flex items-center justify-between gap-2 p-2 mb-1 rounded-md cursor-pointer transition-all duration-200 ease-in-out hover:bg-muted/50 hover:scale-[1.02] ${
                  isSelected ? '' : 'bg-card active:bg-muted'
                }`}
                style={{
                  backgroundColor: isSelected ? '#F2F3FF' : '',
                  color: isSelected ? '#332066' : '',
                  fontWeight: isSelected ? '600' : '',
                  border: isSelected ? '1px solid #5236C4' : '',
                  boxShadow: isSelected ? '0 4px 6px -1px rgba(82, 54, 196, 0.1)' : ''
                }}
              >
                <div 
                  className="flex items-center gap-2 flex-1"
                  onClick={() => {
                    if (isSelected) {
                      setSelectedTags(selectedTags.filter(id => id !== tag.id));
                    } else {
                      setSelectedTags([...selectedTags, tag.id]);
                    }
                  }}
                >
                  <Tag className="h-4 w-4" />
                  <span className="text-sm">{tag.name}</span>
                  <span className="text-xs opacity-70 ml-auto">({tagNoteCount})</span>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteTag(tag.id);
                  }}
                  className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1 hover:bg-red-100 rounded"
                  style={{ color: '#dc2626' }}
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            );
          })}

          {/* New Tag Input */}
          <div className="mt-3 space-y-2">
            <Input
              type="text"
              placeholder="New tag..."
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && createTag()}
              className="text-sm"
            />
            <Button
              onClick={createTag}
              className="w-full transition-all-smooth hover-scale-102"
              variant="secondary"
              size="sm"
            >
              <Plus className="h-4 w-4" />
              Add Tag
            </Button>
          </div>
        </div>

         {/* Logout Section */}
         <div style={{ 
           display: 'flex', 
           flexDirection: 'column',
           gap: '10px',
           marginTop: '20px',
           marginBottom: '20px',
           padding: '10px',
           backgroundColor: '#f0f0f0',
           borderRadius: '5px'
         }}>
           <span>Logged in as: <strong>{session?.user?.email}</strong></span>
           <button
             onClick={async () => {
               try {
                 console.log('Logout button clicked');
                 
                 // Clear the session state first
                 setSession(null);
                 
                 // Try to sign out, but don't fail if there's no session
                 const { error } = await supabase.auth.signOut();
                 if (error && !error.message.includes('Auth session missing')) {
                   console.error('Logout error:', error);
                   alert('Logout failed: ' + error.message);
                 } else {
                   console.log('Logout successful or session already cleared');
                   // Force redirect to login by clearing session
                   setSession(null);
                 }
               } catch (err) {
                 console.error('Logout exception:', err);
                 // Even if there's an error, clear the session locally
                 setSession(null);
                 console.log('Session cleared locally despite error');
               }
             }}
             style={{
               padding: '8px 15px',
               backgroundColor: '#f44336',
               color: 'white',
               border: 'none',
               borderRadius: '5px',
               cursor: 'pointer',
               alignSelf: 'flex-start'
             }}
           >
             Logout
           </button>
         </div>
      </div>
  
      {/* Secondary Sidebar for Notes List */}
      <div className="border-r bg-muted/20 p-4 overflow-y-auto flex-shrink-0" style={{ width: '300px', minWidth: '300px' }}>
        <h2 className="text-xl font-bold mb-4">Notes ({filteredNotes.length})</h2>
        
        {/* Search Box */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search notes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 text-sm"
            />
          </div>
        </div>

        {/* Notes List */}
        <div className="space-y-2" ref={notesListRef}>
          {filteredNotes.map(note => (
            <div key={note.id} className="relative">
              {/* Delete Button - Circular button that appears when dragging left */}
              <div 
                className="absolute right-2 top-1/2 transform -translate-y-1/2 transition-all duration-200 ease-in-out"
                style={{
                  zIndex: 1000,
                  transform: swipedNoteId === note.id 
                    ? 'translateX(0) translateY(-50%)' 
                    : 'translateX(100%) translateY(-50%)',
                  opacity: swipedNoteId === note.id ? 1 : 0
                }}
              >
                <Button
                  onClick={() => {
                    console.log('Delete button clicked for note:', note.id);
                    deleteNote(note.id);
                    setSwipedNoteId(null);
                  }}
                  variant="destructive"
                  size="icon"
                  className="h-10 w-10 rounded-full bg-red-500 hover:bg-red-600 border-2 border-red-600 shadow-lg"
                >
                  <Trash2 className="h-5 w-5 text-white" />
                </Button>
              </div>
              
              {/* Note Card */}
              <div
                data-note-id={note.id}
                className={`p-4 rounded-md cursor-pointer transition-all-smooth hover-scale-102 border relative ${
                  selectedNote?.id === note.id 
                    ? '' 
                    : 'bg-card border-border'
                } ${
                  swipedNoteId === note.id ? 'translate-x-[-56px]' : ''
                } ${
                  dragState.isDragging && dragState.noteId === note.id ? 'cursor-grabbing' : ''
                }`}
                style={{
                  backgroundColor: dragState.isDragging && dragState.noteId === note.id && (dragState.startX - dragState.currentX) > 100
                    ? '#FEF2F2' // Light red background when approaching delete threshold
                    : selectedNote?.id === note.id ? '#F2F3FF' : '',
                  color: selectedNote?.id === note.id ? '#332066' : '',
                  borderColor: selectedNote?.id === note.id ? '#5236C4' : '',
                  boxShadow: selectedNote?.id === note.id ? '0 4px 6px -1px rgba(82, 54, 196, 0.1)' : '',
                  transform: dragState.isDragging && dragState.noteId === note.id 
                    ? `translateX(-${Math.min(Math.max(dragState.startX - dragState.currentX, 0), 150)}px)` 
                    : ''
                }}
                onMouseDown={(e) => handleSwipeStart(e, note.id)}
                onTouchStart={(e) => handleSwipeStart(e, note.id)}
                onTouchMove={(e) => handleSwipeMove(e)}
                onTouchEnd={(e) => handleSwipeEnd(e)}
                onClick={(e) => {
                  // Prevent click if we just finished dragging
                  if (dragState.isDragging) {
                    e.preventDefault();
                    return;
                  }
                  
                  if (swipedNoteId === note.id) {
                    setSwipedNoteId(null);
                  } else {
                    selectNote(note);
                  }
                }}
                onDoubleClick={(e) => {
                  e.preventDefault();
                  console.log('Double click - showing delete button for note:', note.id);
                  setSwipedNoteId(note.id);
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div 
                      className="font-medium text-sm truncate mb-1"
                      style={{
                        color: selectedNote?.id === note.id ? '#332066' : ''
                      }}
                    >
                      {note.title}
                    </div>
                    <div 
                      className="text-xs truncate"
                      style={{
                        color: selectedNote?.id === note.id ? '#5236C4' : '',
                        opacity: selectedNote?.id === note.id ? 1 : 0.7
                      }}
                    >
                      {note.content ? note.content.replace(/<[^>]*>/g, '').substring(0, 60) + '...' : 'No content'}
                    </div>
                    <div 
                      className="text-xs mt-1"
                      style={{
                        color: selectedNote?.id === note.id ? '#332066' : '',
                        opacity: selectedNote?.id === note.id ? 1 : 0.6
                      }}
                    >
                      {new Date(note.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Content Area - Note Editor */}
      <div className="flex-1 flex flex-col overflow-x-hidden" style={{ minWidth: 0, width: 'calc(100vw - 540px)', maxWidth: 'calc(100vw - 540px)' }}>
        {/* Note Editor Content */}
        <div className="flex-1 overflow-x-hidden">
          {selectedNote ? (
            <div className="h-full flex flex-col">
              <NoteEditor
                note={selectedNote}
                folders={folders}
                tags={tags}
                noteTags={noteTags}
                onClose={() => setSelectedNote(null)}
                onSave={handleNoteSave}
                onDelete={handleNoteDelete}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              <div className="text-center">
                <Edit2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-medium mb-2">No note selected</h3>
                <p className="text-sm">Select a note from the list to start editing</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>

    {/* Delete Folder Modal - Outside main container */}
    {deleteFolderModal.isOpen && (
      <div 
        className="fixed inset-0 flex items-center justify-center"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 999999,
          backgroundColor: 'rgba(0, 0, 0, 0.6)'
        }}
        onClick={() => setDeleteFolderModal({ isOpen: false, folderId: null, folderName: '', noteCount: 0 })}
      >
        <div 
          className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl relative"
          style={{
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            zIndex: 1000000,
            position: 'relative'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="text-lg font-semibold mb-4">
            Delete Folder "{deleteFolderModal.folderName}"
          </h3>
          <p className="text-gray-600 mb-4">
            This folder contains {deleteFolderModal.noteCount} note(s). What would you like to do with them?
          </p>
          
          <div className="space-y-3">
            {/* Option 1: Move to All Notes */}
            <button
              onClick={() => {
                performFolderDeletion(deleteFolderModal.folderId, deleteFolderModal.folderName, null);
                setDeleteFolderModal({ isOpen: false, folderId: null, folderName: '', noteCount: 0 });
              }}
              className="w-full p-3 text-left border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              <div className="font-medium">Move to "All Notes"</div>
              <div className="text-sm text-gray-500">Keep all notes, move them to the general notes list</div>
            </button>

            {/* Option 2: Move to another folder */}
            {folders.filter(f => f.id !== deleteFolderModal.folderId).length > 0 && (
              <div className="space-y-2">
                <div className="font-medium text-sm text-gray-700">Move to another folder:</div>
                {folders.filter(f => f.id !== deleteFolderModal.folderId).map(folder => (
                  <button
                    key={folder.id}
                    onClick={() => {
                      performFolderDeletion(deleteFolderModal.folderId, deleteFolderModal.folderName, folder.id);
                      setDeleteFolderModal({ isOpen: false, folderId: null, folderName: '', noteCount: 0 });
                    }}
                    className="w-full p-2 text-left border border-gray-300 rounded-md hover:bg-gray-50 transition-colors text-sm"
                  >
                    Move to "{folder.name}"
                  </button>
                ))}
              </div>
            )}

            {/* Option 3: Delete everything */}
            <button
              onClick={() => {
                performFolderDeletion(deleteFolderModal.folderId, deleteFolderModal.folderName, null, true);
                setDeleteFolderModal({ isOpen: false, folderId: null, folderName: '', noteCount: 0 });
              }}
              className="w-full p-3 text-left border border-red-300 rounded-md hover:bg-red-50 transition-colors text-red-600"
            >
              <div className="font-medium">Delete everything</div>
              <div className="text-sm">Delete the folder and all {deleteFolderModal.noteCount} notes inside it</div>
            </button>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <button
              onClick={() => setDeleteFolderModal({ isOpen: false, folderId: null, folderName: '', noteCount: 0 })}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

function AppWithToast() {
  return (
    <ToastProvider>
      <App />
    </ToastProvider>
  );
}

export default AppWithToast;
